// IND-022: producción del año en Datos de planta (tabla «Producción por mes»
// para comparar con el ERP y lista de festivos) y botón «Marcar como festivo».
// Los festivos solo quitan el aviso de día sin producción: no cambian plazos
// ni ningún cálculo.
import { useState } from 'react';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  MenuItem,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';
import { EventBusyRounded } from '@mui/icons-material';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import {
  getProductionByMonth,
  listPlantHolidays,
  markPlantHoliday,
  unmarkPlantHoliday,
} from '../services/industrialCostService';
import type {
  PlantDataFreshness,
  PlantDataReviewResponse,
  PlantHoliday,
  ProductionMonthRow,
  ProductionMonthTotals,
  ProductionMonthUpload,
} from '../services/industrialCostService';
import { formatDataDay } from '../utils/dailyLoadRunFile';
import { formatEsNumber } from '../utils/numberFormat';
import { sortRows, useTableSort } from '../utils/tableSort';
import type { SortValue } from '../utils/tableSort';
import SortableHeaderCell from '../components/SortableHeaderCell';

// Literales (no se importan de otras vistas para evitar ciclos).
const REVIEW_KEY = ['industrial-cost-plant-data-review'];
const FRESHNESS_KEY = ['industrial-cost-plant-data-freshness'];
const HOLIDAYS_KEY = ['industrial-cost-plant-holidays'];
const BY_MONTH_KEY = ['industrial-cost-production-by-month'];

const MONTHS = [
  'Enero', 'Febrero', 'Marzo', 'Abril', 'Mayo', 'Junio',
  'Julio', 'Agosto', 'Septiembre', 'Octubre', 'Noviembre', 'Diciembre',
];

function errorText(error: unknown, fallback: string): string {
  const detail = (error as { response?: { data?: { detail?: unknown } } })?.response?.data?.detail;
  return typeof detail === 'string' ? detail : fallback;
}

function useRefreshAfterHolidayChange() {
  const queryClient = useQueryClient();
  return (holidayDate: string, marked: boolean) => {
    if (marked) {
      // Deja de avisar al momento, sin esperar a recalcular el informe.
      queryClient.setQueryData<PlantDataReviewResponse>(REVIEW_KEY, (current) =>
        current
          ? {
              ...current,
              notices: (current.notices ?? []).filter(
                (notice) => notice.id !== `dia_laborable_sin_produccion:${holidayDate}`,
              ),
            }
          : current,
      );
      queryClient.setQueryData<PlantDataFreshness>(FRESHNESS_KEY, (current) =>
        current
          ? {
              ...current,
              warnings: current.warnings
                .map((warning) =>
                  warning.code === 'workdays_without_production'
                    ? { ...warning, dates: (warning.dates ?? []).filter((day) => day !== holidayDate) }
                    : warning,
                )
                .filter((warning) => warning.code !== 'workdays_without_production' || (warning.dates ?? []).length > 0),
            }
          : current,
      );
    }
    void queryClient.invalidateQueries({ queryKey: REVIEW_KEY });
    void queryClient.invalidateQueries({ queryKey: FRESHNESS_KEY });
    void queryClient.invalidateQueries({ queryKey: HOLIDAYS_KEY });
    void queryClient.invalidateQueries({ queryKey: BY_MONTH_KEY });
  };
}

/** «Marcar como festivo» de un día laborable sin producción (nombre opcional). */
export function MarkHolidayButton({ day }: { day: string }) {
  const [editing, setEditing] = useState(false);
  const [name, setName] = useState('');
  const refresh = useRefreshAfterHolidayChange();
  const mutation = useMutation({
    mutationFn: () => markPlantHoliday({ holiday_date: day, name: name.trim() || null }),
    onSuccess: () => {
      setEditing(false);
      refresh(day, true);
    },
  });

  if (!editing) {
    return (
      <Button
        size="small"
        variant="text"
        startIcon={<EventBusyRounded />}
        onClick={(event) => {
          event.stopPropagation();
          setEditing(true);
        }}
        sx={{ textTransform: 'none', fontSize: 12, px: 0.5 }}
      >
        Marcar como festivo
      </Button>
    );
  }
  return (
    <Stack direction="row" spacing={0.75} alignItems="center" flexWrap="wrap" useFlexGap onClick={(event) => event.stopPropagation()}>
      <TextField
        size="small"
        placeholder="Nombre (opcional), p. ej. Pilar"
        value={name}
        onChange={(event) => setName(event.target.value.slice(0, 80))}
        sx={{ width: 220, '& input': { fontSize: 12, py: 0.6 } }}
        autoFocus
      />
      <Button
        size="small"
        variant="contained"
        disabled={mutation.isPending}
        onClick={() => mutation.mutate()}
        sx={{ textTransform: 'none', fontSize: 12 }}
      >
        {mutation.isPending ? <CircularProgress size={12} /> : 'Marcar'}
      </Button>
      <Button size="small" onClick={() => setEditing(false)} sx={{ textTransform: 'none', fontSize: 12 }}>
        Cancelar
      </Button>
      {mutation.isError ? (
        <Typography sx={{ color: '#f87171', fontSize: 12 }}>
          {errorText(mutation.error, 'No se pudo marcar el día como festivo.')}
        </Typography>
      ) : null}
    </Stack>
  );
}

function fmt(value: number | null | undefined, decimals = 0): string {
  return value == null ? '—' : formatEsNumber(value, { maximumFractionDigits: decimals });
}

function kgText(row: ProductionMonthTotals): string {
  const kg = row.kg == null ? '—' : fmt(row.kg);
  return row.rows_unconverted > 0 ? `${kg} · ${fmt(row.rows_unconverted)} sin convertir` : kg;
}

function otherRowsText(row: ProductionMonthTotals): string {
  const parts = [
    row.manual_rows ? `${fmt(row.manual_rows)} manuales` : null,
    row.monthly_rows ? `${fmt(row.monthly_rows)} mensuales` : null,
  ].filter(Boolean);
  return parts.length > 0 ? parts.join(' · ') : '—';
}

function uploadText(upload: ProductionMonthUpload | null): string {
  if (!upload) return '—';
  const loaded = upload.uploaded_at
    ? new Intl.DateTimeFormat('es-ES', { dateStyle: 'short' }).format(new Date(upload.uploaded_at))
    : '—';
  const range =
    upload.date_from && upload.date_to
      ? ` (${formatDataDay(upload.date_from)?.slice(0, 5)}–${formatDataDay(upload.date_to)?.slice(0, 5)})`
      : '';
  return `${loaded}${range}`;
}

// IND-026: columnas ordenables. Por defecto, los meses en orden (enero a
// diciembre) y los festivos del más reciente al más antiguo. La fila Total
// queda siempre al final.
type MonthSortKey =
  | 'month' | 'meters' | 'kg' | 'parts' | 'days' | 'gaps' | 'holidays' | 'other' | 'upload';
const MONTH_COLUMNS: Array<{ key: MonthSortKey; label: string; value: (row: ProductionMonthRow) => SortValue }> = [
  { key: 'month', label: 'Mes', value: (row) => row.month },
  { key: 'meters', label: 'Metros', value: (row) => row.meters },
  { key: 'kg', label: 'Kg', value: (row) => row.kg },
  { key: 'parts', label: 'Partes', value: (row) => row.parts },
  { key: 'days', label: 'Días con producción', value: (row) => row.days_with_production },
  { key: 'gaps', label: 'Laborables sin producción', value: (row) => row.workdays_without_production },
  { key: 'holidays', label: 'Festivos', value: (row) => row.holidays },
  { key: 'other', label: 'Otras vías', value: (row) => (row.manual_rows ?? 0) + (row.monthly_rows ?? 0) || null },
  { key: 'upload', label: 'Última carga', value: (row) => row.last_upload?.uploaded_at },
];

type HolidaySortKey = 'date' | 'name' | 'by' | 'when';
const HOLIDAY_COLUMNS: Array<{ key: HolidaySortKey; label: string; value: (row: PlantHoliday) => SortValue }> = [
  { key: 'date', label: 'Fecha', value: (row) => row.holiday_date },
  { key: 'name', label: 'Nombre', value: (row) => row.name },
  { key: 'by', label: 'Marcado por', value: (row) => row.created_by_label },
  { key: 'when', label: 'Cuándo', value: (row) => row.created_at },
];

const HEAD_SX = { bgcolor: '#0f172a', color: '#94a3b8', fontSize: 12, py: 0.75, whiteSpace: 'nowrap' } as const;
const CELL_SX = { color: '#e2e8f0', fontSize: 12.5, py: 0.6, fontVariantNumeric: 'tabular-nums', whiteSpace: 'nowrap' } as const;

/** Tabla «Producción por mes» y lista de festivos del año elegido. */
export function PlantProductionYearBlock() {
  const currentYear = new Date().getFullYear();
  const [year, setYear] = useState(currentYear);
  const refresh = useRefreshAfterHolidayChange();

  const byMonthQuery = useQuery({
    queryKey: [...BY_MONTH_KEY, year],
    queryFn: () => getProductionByMonth(year),
    staleTime: 60_000,
    retry: false,
  });
  const holidaysQuery = useQuery({
    queryKey: [...HOLIDAYS_KEY, year],
    queryFn: () => listPlantHolidays(year),
    staleTime: 60_000,
    retry: false,
  });
  const unmark = useMutation({
    mutationFn: (holidayDate: string) => unmarkPlantHoliday(holidayDate),
    onSuccess: (_data, holidayDate) => refresh(holidayDate, false),
  });

  const monthSort = useTableSort<MonthSortKey>('month', 'asc');
  const holidaySort = useTableSort<HolidaySortKey>('date', 'desc');
  const data = byMonthQuery.data;
  const monthColumn = MONTH_COLUMNS.find((column) => column.key === monthSort.key) ?? MONTH_COLUMNS[0];
  const months = data ? sortRows(data.months, monthColumn.value, monthSort.direction) : [];
  const holidayColumn = HOLIDAY_COLUMNS.find((column) => column.key === holidaySort.key) ?? HOLIDAY_COLUMNS[0];
  const holidays = sortRows(holidaysQuery.data ?? [], holidayColumn.value, holidaySort.direction);

  return (
    <Paper sx={{ borderRadius: 4, bgcolor: 'rgba(15,23,42,0.72)', border: '1px solid rgba(148,163,184,0.16)' }}>
      <Stack spacing={1.5} sx={{ p: 2.5 }}>
        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} justifyContent="space-between" alignItems={{ sm: 'center' }}>
          <Box>
            <Typography sx={{ color: '#f8fafc', fontSize: 18, fontWeight: 900 }}>Producción por mes</Typography>
            <Typography sx={{ color: '#94a3b8', fontSize: 12 }}>
              Partes del ERP cargados, mes a mes, para compararlo con el listado del ERP.
            </Typography>
          </Box>
          <TextField
            select
            size="small"
            label="Año"
            value={year}
            onChange={(event) => setYear(Number(event.target.value))}
            sx={{ width: 120 }}
          >
            {[currentYear, currentYear - 1, currentYear - 2].map((value) => (
              <MenuItem key={value} value={value}>
                {value}
              </MenuItem>
            ))}
          </TextField>
        </Stack>

        {byMonthQuery.isLoading ? (
          <CircularProgress size={20} />
        ) : byMonthQuery.isError ? (
          <Alert severity="error" variant="outlined">
            No se pudo calcular la producción del año.
          </Alert>
        ) : data ? (
          <Box sx={{ overflowX: 'auto' }}>
            {data.granularity === 'year' ? (
              <Typography sx={{ color: '#fbbf24', fontSize: 12, mb: 0.5 }}>
                Este año solo tiene producción anual: no hay desglose por mes.
              </Typography>
            ) : null}
            <Table size="small" sx={{ '& td, & th': { borderColor: 'rgba(148,163,184,0.12)' } }}>
              <TableHead>
                <TableRow>
                  {MONTH_COLUMNS.map((column) => (
                    <SortableHeaderCell
                      key={column.key}
                      label={column.label}
                      columnKey={column.key}
                      sort={monthSort}
                      align={column.key === 'month' ? 'left' : 'right'}
                      firstDirection={column.key === 'month' ? 'asc' : 'desc'}
                      sx={HEAD_SX}
                    />
                  ))}
                </TableRow>
              </TableHead>
              <TableBody>
                {months.map((row) => (
                  <TableRow key={row.month}>
                    <TableCell sx={{ ...CELL_SX, fontWeight: 700 }}>{MONTHS[row.month - 1]}</TableCell>
                    <TableCell sx={CELL_SX} align="right">{fmt(row.meters)}</TableCell>
                    <TableCell sx={CELL_SX} align="right">{kgText(row)}</TableCell>
                    <TableCell sx={CELL_SX} align="right">{fmt(row.parts)}</TableCell>
                    <TableCell sx={CELL_SX} align="right">{fmt(row.days_with_production)}</TableCell>
                    <TableCell
                      sx={{ ...CELL_SX, color: (row.workdays_without_production ?? 0) > 0 ? '#fbbf24' : CELL_SX.color }}
                      align="right"
                    >
                      {fmt(row.workdays_without_production)}
                    </TableCell>
                    <TableCell sx={CELL_SX} align="right">{fmt(row.holidays)}</TableCell>
                    <TableCell sx={{ ...CELL_SX, color: '#94a3b8' }} align="right">{otherRowsText(row)}</TableCell>
                    <TableCell sx={{ ...CELL_SX, color: '#94a3b8' }} align="right">{uploadText(row.last_upload)}</TableCell>
                  </TableRow>
                ))}
                <TableRow>
                  <TableCell sx={{ ...CELL_SX, fontWeight: 900, color: '#f8fafc' }}>{`Total ${data.year}`}</TableCell>
                  <TableCell sx={{ ...CELL_SX, fontWeight: 900, color: '#f8fafc' }} align="right">{fmt(data.total.meters)}</TableCell>
                  <TableCell sx={{ ...CELL_SX, fontWeight: 900, color: '#f8fafc' }} align="right">{kgText(data.total)}</TableCell>
                  <TableCell sx={{ ...CELL_SX, fontWeight: 900 }} align="right">{fmt(data.total.parts)}</TableCell>
                  <TableCell sx={{ ...CELL_SX, fontWeight: 900 }} align="right">{fmt(data.total.days_with_production)}</TableCell>
                  <TableCell sx={{ ...CELL_SX, fontWeight: 900 }} align="right">{fmt(data.total.workdays_without_production)}</TableCell>
                  <TableCell sx={{ ...CELL_SX, fontWeight: 900 }} align="right">{fmt(data.total.holidays)}</TableCell>
                  <TableCell sx={{ ...CELL_SX, color: '#94a3b8' }} align="right">{otherRowsText(data.total)}</TableCell>
                  <TableCell sx={CELL_SX} />
                </TableRow>
              </TableBody>
            </Table>
            {data.window.to ? (
              <Typography sx={{ color: '#64748b', fontSize: 11.5, mt: 0.5 }}>
                {`Días laborables sin producción del ${formatDataDay(data.window.from)} al ${formatDataDay(data.window.to)} (último día con partes diarios). Sábados y domingos no cuentan.`}
              </Typography>
            ) : null}
          </Box>
        ) : null}

        <Box>
          <Typography sx={{ color: '#f8fafc', fontSize: 14, fontWeight: 850 }}>{`Festivos marcados en ${year}`}</Typography>
          <Typography sx={{ color: '#94a3b8', fontSize: 12 }}>
            Solo quitan el aviso de día sin producción; no cambian los plazos ni ningún cálculo.
          </Typography>
          {holidays.length === 0 ? (
            <Typography sx={{ color: '#64748b', fontSize: 12, mt: 0.5 }}>Ningún día marcado como festivo.</Typography>
          ) : (
            <Table size="small" sx={{ mt: 0.5, '& td, & th': { borderColor: 'rgba(148,163,184,0.12)' } }}>
              <TableHead>
                <TableRow>
                  {HOLIDAY_COLUMNS.map((column) => (
                    <SortableHeaderCell
                      key={column.key}
                      label={column.label}
                      columnKey={column.key}
                      sort={holidaySort}
                      firstDirection={column.key === 'name' || column.key === 'by' ? 'asc' : 'desc'}
                      sx={HEAD_SX}
                    />
                  ))}
                  <TableCell sx={HEAD_SX} />
                </TableRow>
              </TableHead>
              <TableBody>
                {holidays.map((holiday) => (
                  <TableRow key={holiday.id}>
                    <TableCell sx={CELL_SX}>{formatDataDay(holiday.holiday_date)}</TableCell>
                    <TableCell sx={CELL_SX}>{holiday.name || '—'}</TableCell>
                    <TableCell sx={CELL_SX}>{holiday.created_by_label || '—'}</TableCell>
                    <TableCell sx={CELL_SX}>
                      {new Intl.DateTimeFormat('es-ES', { dateStyle: 'short', timeStyle: 'short' }).format(new Date(holiday.created_at))}
                    </TableCell>
                    <TableCell sx={CELL_SX} align="right">
                      <Button
                        size="small"
                        disabled={unmark.isPending}
                        onClick={() => unmark.mutate(holiday.holiday_date)}
                        sx={{ textTransform: 'none', fontSize: 12 }}
                      >
                        Quitar
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
          {unmark.isError ? (
            <Typography sx={{ color: '#f87171', fontSize: 12 }}>
              {errorText(unmark.error, 'No se pudo quitar el festivo.')}
            </Typography>
          ) : null}
        </Box>
      </Stack>
    </Paper>
  );
}
